/**
 * CoverDesigner
 * Visual book cover designer component
 */

import { useState } from 'react'
import {
  CoverDesign,
  DEFAULT_COVER_DESIGN,
  COVER_FONTS,
  COVER_GRADIENTS,
} from '@/core/models'
import { Button, Input, Select, Card, Badge } from '../ui'
import { clsx } from 'clsx'
import { Palette, Type, Sparkles } from 'lucide-react'

export interface CoverDesignerProps {
  bookTitle: string
  bookAuthor: string
  initialDesign?: Partial<CoverDesign>
  onSave: (design: Omit<CoverDesign, 'id' | 'bookId' | 'createdAt' | 'updatedAt'>) => void
}

export function CoverDesigner({
  bookTitle,
  bookAuthor,
  initialDesign,
  onSave,
}: CoverDesignerProps) {
  const [design, setDesign] = useState<Omit<CoverDesign, 'id' | 'bookId' | 'createdAt' | 'updatedAt'>>({
    ...DEFAULT_COVER_DESIGN,
    ...initialDesign,
    title: {
      ...DEFAULT_COVER_DESIGN.title,
      ...initialDesign?.title,
      text: bookTitle,
    },
    author: {
      ...DEFAULT_COVER_DESIGN.author,
      ...initialDesign?.author,
      text: bookAuthor,
    },
  })

  const [activeTab, setActiveTab] = useState<'background' | 'text' | 'preview'>('background')

  const updateDesign = (updates: Partial<typeof design>) => {
    setDesign(prev => ({ ...prev, ...updates }))
  }

  const updateTitle = (updates: Partial<typeof design.title>) => {
    setDesign(prev => ({
      ...prev,
      title: { ...prev.title, ...updates },
    }))
  }

  const updateAuthor = (updates: Partial<typeof design.author>) => {
    setDesign(prev => ({
      ...prev,
      author: { ...prev.author, ...updates },
    }))
  }

  const getBackgroundStyle = () => {
    const style: React.CSSProperties = {}

    if (design.backgroundType === 'color' && design.backgroundColor) {
      style.background = design.backgroundColor
    } else if (design.backgroundType === 'gradient' && design.gradientColors) {
      const direction =
        design.gradientDirection === 'diagonal'
          ? 'to bottom right'
          : design.gradientDirection?.replace('to-', 'to ')
      style.background = `linear-gradient(${direction}, ${design.gradientColors[0]}, ${design.gradientColors[1]})`
    } else if (design.backgroundType === 'image' && design.backgroundImage) {
      style.backgroundImage = `url(${design.backgroundImage})`
      style.backgroundSize = 'cover'
      style.backgroundPosition = 'center'
    }

    return style
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Preview Panel */}
      <div className="order-2 lg:order-1">
        <div className="sticky top-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <Sparkles className="h-4 w-4" />
            Live Preview
          </h3>
          <div className="bg-gray-100 p-8 rounded-lg flex items-center justify-center">
            <div
              className="shadow-2xl relative overflow-hidden"
              style={{
                width: design.dimensions.width / 2,
                height: design.dimensions.height / 2,
                ...getBackgroundStyle(),
              }}
            >
              {/* Title */}
              <div
                className="absolute"
                style={{
                  left: `${design.title.position.x}%`,
                  top: `${design.title.position.y}%`,
                  transform: 'translate(-50%, -50%)',
                  width: '90%',
                }}
              >
                <h1
                  style={{
                    fontFamily: design.title.font,
                    fontSize: design.title.fontSize / 2,
                    color: design.title.color,
                    textAlign: design.title.align,
                    fontWeight: design.title.bold ? 'bold' : 'normal',
                    fontStyle: design.title.italic ? 'italic' : 'normal',
                    textShadow: design.title.shadow ? '2px 2px 4px rgba(0,0,0,0.5)' : 'none',
                  }}
                >
                  {design.title.text || 'Your Book Title'}
                </h1>
              </div>

              {/* Author */}
              <div
                className="absolute"
                style={{
                  left: `${design.author.position.x}%`,
                  top: `${design.author.position.y}%`,
                  transform: 'translate(-50%, -50%)',
                  width: '90%',
                }}
              >
                <p
                  style={{
                    fontFamily: design.author.font,
                    fontSize: design.author.fontSize / 2,
                    color: design.author.color,
                    textAlign: design.author.align,
                    fontWeight: design.author.bold ? 'bold' : 'normal',
                    fontStyle: design.author.italic ? 'italic' : 'normal',
                    textShadow: design.author.shadow ? '2px 2px 4px rgba(0,0,0,0.5)' : 'none',
                  }}
                >
                  {design.author.text || 'Author Name'}
                </p>
              </div>
            </div>
          </div>

          <div className="mt-4">
            <Button variant="primary" fullWidth onClick={() => onSave(design)}>
              Save Cover Design
            </Button>
          </div>
        </div>
      </div>

      {/* Controls Panel */}
      <div className="order-1 lg:order-2">
        {/* Tabs */}
        <div className="flex gap-2 mb-4">
          <Button
            variant={activeTab === 'background' ? 'primary' : 'ghost'}
            size="sm"
            onClick={() => setActiveTab('background')}
          >
            <Palette className="h-4 w-4 mr-2" />
            Background
          </Button>
          <Button
            variant={activeTab === 'text' ? 'primary' : 'ghost'}
            size="sm"
            onClick={() => setActiveTab('text')}
          >
            <Type className="h-4 w-4 mr-2" />
            Text
          </Button>
        </div>

        {/* Background Tab */}
        {activeTab === 'background' && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Background Type
              </label>
              <div className="grid grid-cols-3 gap-2">
                {(['color', 'gradient', 'image'] as const).map(type => (
                  <button
                    key={type}
                    onClick={() => updateDesign({ backgroundType: type })}
                    className={clsx(
                      'p-3 rounded-lg border-2 transition-all capitalize',
                      design.backgroundType === type
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-blue-300',
                    )}
                  >
                    {type}
                  </button>
                ))}
              </div>
            </div>

            {design.backgroundType === 'color' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Background Color
                </label>
                <Input
                  type="color"
                  value={design.backgroundColor || '#667EEA'}
                  onChange={e => updateDesign({ backgroundColor: e.target.value })}
                  fullWidth
                />
              </div>
            )}

            {design.backgroundType === 'gradient' && (
              <div className="space-y-3">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Choose Gradient
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {COVER_GRADIENTS.map(gradient => (
                    <button
                      key={gradient.id}
                      onClick={() =>
                        updateDesign({
                          gradientColors: gradient.colors,
                        })
                      }
                      className={clsx(
                        'h-16 rounded-lg border-2 transition-all relative overflow-hidden',
                        design.gradientColors?.[0] === gradient.colors[0]
                          ? 'border-blue-500 ring-2 ring-blue-200'
                          : 'border-gray-200 hover:border-blue-300',
                      )}
                      style={{
                        background: `linear-gradient(to bottom right, ${gradient.colors[0]}, ${gradient.colors[1]})`,
                      }}
                    >
                      <span className="absolute bottom-1 left-2 text-xs text-white font-medium drop-shadow">
                        {gradient.name}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {design.backgroundType === 'image' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Image URL
                </label>
                <Input
                  type="url"
                  placeholder="https://example.com/image.jpg"
                  value={design.backgroundImage || ''}
                  onChange={e => updateDesign({ backgroundImage: e.target.value })}
                  fullWidth
                />
                <p className="text-xs text-gray-500 mt-1">
                  Enter an image URL or upload to an image hosting service
                </p>
              </div>
            )}
          </div>
        )}

        {/* Text Tab */}
        {activeTab === 'text' && (
          <div className="space-y-6">
            {/* Title Section */}
            <Card padding="sm">
              <div className="mb-3">
                <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                  Title
                  <Badge variant="default" className="text-xs">
                    Main
                  </Badge>
                </h4>
              </div>
              <div className="space-y-3">
                <Input
                  label="Text"
                  value={design.title.text}
                  onChange={e => updateTitle({ text: e.target.value })}
                  fullWidth
                />

                <div className="grid grid-cols-2 gap-3">
                  <Select
                    label="Font"
                    value={design.title.font}
                    onChange={e => updateTitle({ font: e.target.value })}
                    fullWidth
                  >
                    {COVER_FONTS.map(font => (
                      <option key={font.id} value={font.family}>
                        {font.name}
                      </option>
                    ))}
                  </Select>

                  <Input
                    label="Size"
                    type="number"
                    value={design.title.fontSize}
                    onChange={e => updateTitle({ fontSize: Number(e.target.value) })}
                    min={12}
                    max={120}
                    fullWidth
                  />
                </div>

                <Input
                  label="Color"
                  type="color"
                  value={design.title.color}
                  onChange={e => updateTitle({ color: e.target.value })}
                  fullWidth
                />

                <div className="flex gap-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={design.title.bold}
                      onChange={e => updateTitle({ bold: e.target.checked })}
                      className="rounded"
                    />
                    <span className="text-sm">Bold</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={design.title.italic}
                      onChange={e => updateTitle({ italic: e.target.checked })}
                      className="rounded"
                    />
                    <span className="text-sm">Italic</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={design.title.shadow}
                      onChange={e => updateTitle({ shadow: e.target.checked })}
                      className="rounded"
                    />
                    <span className="text-sm">Shadow</span>
                  </label>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <Input
                    label="Position Y (%)"
                    type="number"
                    value={design.title.position.y}
                    onChange={e =>
                      updateTitle({
                        position: { ...design.title.position, y: Number(e.target.value) },
                      })
                    }
                    min={0}
                    max={100}
                    fullWidth
                  />
                  <Select
                    label="Align"
                    value={design.title.align}
                    onChange={e =>
                      updateTitle({ align: e.target.value as 'left' | 'center' | 'right' })
                    }
                    fullWidth
                  >
                    <option value="left">Left</option>
                    <option value="center">Center</option>
                    <option value="right">Right</option>
                  </Select>
                </div>
              </div>
            </Card>

            {/* Author Section */}
            <Card padding="sm">
              <div className="mb-3">
                <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                  Author
                  <Badge variant="default" className="text-xs">
                    Subtitle
                  </Badge>
                </h4>
              </div>
              <div className="space-y-3">
                <Input
                  label="Text"
                  value={design.author.text}
                  onChange={e => updateAuthor({ text: e.target.value })}
                  fullWidth
                />

                <div className="grid grid-cols-2 gap-3">
                  <Select
                    label="Font"
                    value={design.author.font}
                    onChange={e => updateAuthor({ font: e.target.value })}
                    fullWidth
                  >
                    {COVER_FONTS.map(font => (
                      <option key={font.id} value={font.family}>
                        {font.name}
                      </option>
                    ))}
                  </Select>

                  <Input
                    label="Size"
                    type="number"
                    value={design.author.fontSize}
                    onChange={e => updateAuthor({ fontSize: Number(e.target.value) })}
                    min={12}
                    max={60}
                    fullWidth
                  />
                </div>

                <Input
                  label="Color"
                  type="color"
                  value={design.author.color}
                  onChange={e => updateAuthor({ color: e.target.value })}
                  fullWidth
                />

                <div className="grid grid-cols-2 gap-3">
                  <Input
                    label="Position Y (%)"
                    type="number"
                    value={design.author.position.y}
                    onChange={e =>
                      updateAuthor({
                        position: { ...design.author.position, y: Number(e.target.value) },
                      })
                    }
                    min={0}
                    max={100}
                    fullWidth
                  />
                  <Select
                    label="Align"
                    value={design.author.align}
                    onChange={e =>
                      updateAuthor({ align: e.target.value as 'left' | 'center' | 'right' })
                    }
                    fullWidth
                  >
                    <option value="left">Left</option>
                    <option value="center">Center</option>
                    <option value="right">Right</option>
                  </Select>
                </div>
              </div>
            </Card>
          </div>
        )}
      </div>
    </div>
  )
}
